import { useCallback, memo, useMemo, useState, useEffect } from 'react'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'

import {
	Box,
	Typography,
	Select,
	MenuItem,
	IconButton,
	Button,
	Grid,
	FormControl,
	Popover,
	TextField,
	ListItemText,
	ListItemButton,
	Collapse,
	List,
	ListSubheader,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import ArrowDropUp from '@mui/icons-material/ArrowDropUp'

import useMenuQuery from '../../hooks/useMenuQuery'
import useDashboardQuery from '../../hooks/useDashboardQuery'

import LoadingSpinner from 'custom-components/LoadingSpinner'

import { isJson } from '../../../../../utils/functions/helpers'
import { useTileFieldsQuery } from './helper/useTileFieldsQuery'

function ModalDashboardSettingComponent(props) {
	const { setting, onChangeSetting, dataFields, showDataFieldSeclect = true, settingTitle } = props
	const [t] = useTranslation()
	// Handle dashboard change
	const handleDashboardChange = useCallback(
		(dashboardKey) => {
			onChangeSetting?.({
				...setting,
				dashboardKey: dashboardKey,
			})
		},
		[setting, onChangeSetting]
	)

	const handleDataFieldChange = useCallback(
		(e) => {
			onChangeSetting?.({
				...setting,
				dataField: e.target.value,
			})
		},
		[setting, onChangeSetting]
	)

	// Handle tileKey change
	const handleTileKeyChange = useCallback(
		(index, value) => {
			const newLinkedTiles = [...(setting?.linkedTiles || [])]
			newLinkedTiles[index] = {
				...newLinkedTiles[index],
				tileKey: value,
			}
			onChangeSetting?.({ ...setting, linkedTiles: newLinkedTiles })
		},
		[setting, onChangeSetting]
	)

	// Handle multiple field change
	const handleFieldsChange = useCallback(
		(tileIndex, selectedValues) => {
			const newLinkedTiles = [...(setting?.linkedTiles || [])]
			newLinkedTiles[tileIndex] = {
				...newLinkedTiles[tileIndex],
				fields: selectedValues,
			}
			onChangeSetting?.({ ...setting, linkedTiles: newLinkedTiles })
		},
		[setting, onChangeSetting]
	)

	// Add new tile
	const handleAddTile = useCallback(() => {
		const newLinkedTiles = [...(setting?.linkedTiles || []), { tileKey: '', fields: [] }]
		onChangeSetting?.({ ...setting, linkedTiles: newLinkedTiles })
	}, [setting, onChangeSetting])

	// Remove tile
	const handleRemoveTile = useCallback(
		(index) => {
			const newLinkedTiles = [...(setting?.linkedTiles || [])]
			newLinkedTiles.splice(index, 1)
			onChangeSetting?.({ ...setting, linkedTiles: newLinkedTiles })
		},
		[setting, onChangeSetting]
	)

	const { dashboardTileList, isLoading } = useDashboardQuery({
		dashboardKey: setting?.dashboardKey,
	})

	return (
		<div style={{ marginBottom: '15px' }}>
			<Typography variant="subtitle2" sx={{ pl: 1 }}>
				{settingTitle || t('common:dis.modalDashboard')}
			</Typography>
			<Box
				sx={{
					width: '100%',
					minHeight: 180,
					border: '1px solid #ddd',
					borderRadius: 1,
					p: 2,
					background: '#fff',
				}}
			>
				{showDataFieldSeclect && (
					<FormControl sx={{ mb: '15px' }} variant="outlined" size="small" fullWidth>
						<Typography variant="subtitle2" sx={{ pl: 1 }}>
							{t('common:dis.modalDashboardRowId')}
						</Typography>

						<Select value={setting?.dataField || ''} onChange={handleDataFieldChange} displayEmpty>
							{dataFields?.map((col) => (
								<MenuItem key={col.name} value={col.name}>
									{col.title || col.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				<FormControl sx={{ mb: '15px' }} variant="outlined" size="small" fullWidth>
					<Typography variant="subtitle2" sx={{ pl: 1 }}>
						{t('common:dis.dashboard')}
					</Typography>

					<DashboardSelectComponent
						selectedDashboardKey={setting?.dashboardKey}
						onDashboardChange={handleDashboardChange}
					/>
				</FormControl>

				<Grid container alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
					<Grid item xs={4} md={4} lg={4}>
						<Typography variant="subtitle2" sx={{ pl: 1, minWidth: '115px' }}>
							{t('common:dis.modalDashboardDashboardTile')}
						</Typography>
					</Grid>
					<Grid item xs={6} md={6} lg={6}>
						<Typography variant="subtitle2" sx={{ pl: 1 }}>
							{t('common:dis.modalDashboardPassValue')}
						</Typography>
					</Grid>
					<Grid item xs={1} md={1} lg={1}></Grid>
				</Grid>

				{setting?.linkedTiles?.map((tile, i) => {
					const selectedTile = dashboardTileList?.find((t) => t.key === tile.tileKey)
					return (
						<Grid key={i} container alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
							<Grid item xs={4} md={4} lg={4}>
								<TileSelectComponent
									loadingDashboard={isLoading}
									dashboardTileList={dashboardTileList}
									selectedTileKey={tile?.tileKey}
									onTileChange={(value) => handleTileKeyChange(i, value)}
								/>
							</Grid>

							<Grid item xs={6} md={6} lg={6}>
								{selectedTile && (
									<TileFieldMultiSelectComponent
										selectedTile={selectedTile}
										selectedFields={tile.fields}
										onFieldsChange={(value) => handleFieldsChange(i, value)}
									/>
								)}
							</Grid>

							<Grid item xs={1} md={1} lg={1}>
								<Button
									aria-label="delete"
									size="small"
									variant="contained"
									sx={{
										borderRadius: '50%',
										width: '25px !important',
										minWidth: '25px !important',
										height: '25px !important',
									}}
									onClick={() => handleRemoveTile(i)}
								>
									<DeleteIcon fontSize="small" />
								</Button>
							</Grid>
						</Grid>
					)
				})}

				<Box display="flex" justifyContent="flex-end">
					<Typography
						variant="body2"
						sx={{
							color: 'primary.main',
							fontWeight: 600,
							cursor: 'pointer',
							userSelect: 'none',
							minWidth: 48,
							textAlign: 'right',
						}}
						onClick={handleAddTile}
					>
						+ {t('common:misc.add')}
					</Typography>
				</Box>
			</Box>
		</div>
	)
}

function DashboardSelectComponent(props) {
	const { onDashboardChange, selectedDashboardKey } = props

	const { menuList, isLoading, menuNameByDashboardKey, onRefreshMenu } = useMenuQuery()

	const [anchorEl, setAnchorEl] = useState(null)
	const isSelectOpen = useMemo(() => {
		return Boolean(anchorEl)
	}, [anchorEl])
	const handleSelectOpen = (event) => {
		setAnchorEl(event.currentTarget)
	}
	const handleSelectClose = () => {
		setAnchorEl(null)
	}

	function parseSettings(settings) {
		if (!settings) return {}
		if (typeof settings === 'string') {
			try {
				return JSON.parse(settings)
			} catch (e) {
				return {}
			}
		}
		return settings
	}

	const menuHasDashboards = useMemo(() => {
		if (isLoading) return []

		return menuList
			.map((menu) => {
				const s = parseSettings(menu.settings)
				const modalKeys = new Set(
					Array.isArray(s.isModalDashboardMenu) ? s.isModalDashboardMenu : []
				)

				const filterItemsRec = (items = []) => {
					const filtered = (items || []).reduce((acc, item) => {
						const children = filterItemsRec(item.items)
						const itemMatches = !!item?.dashboard?.key && modalKeys.has(item.dashboard.key)

						if (itemMatches || (children && children.length)) {
							acc.push({
								...item,
								items: children,
							})
						}
						return acc
					}, [])
					return filtered
				}

				const filteredItems = filterItemsRec(menu.items)

				return {
					...menu,
					items: filteredItems,
				}
			})
			.filter((menu) => Array.isArray(menu.items) && menu.items.length > 0)
	}, [isLoading, menuList])

	const displayedDashboardName = useMemo(() => {
		if (isLoading) return ''
		return menuNameByDashboardKey[selectedDashboardKey] || ''
	}, [isLoading, menuNameByDashboardKey, selectedDashboardKey])
	const [collapseMenus, setCollapseMenus] = useState([])

	const handleMenuCollapsed = (menuKey) => {
		const found = collapseMenus.includes(menuKey)
		if (!found) {
			setCollapseMenus([...collapseMenus, menuKey])
		} else {
			setCollapseMenus(collapseMenus.filter((item) => item !== menuKey))
		}
	}

	const handleSelectDashboard = (dashboardKey) => {
		onDashboardChange?.(dashboardKey)
		handleSelectClose()
	}

	return (
		<Box>
			<TextField
				id="select-default-form"
				size="small"
				onClick={handleSelectOpen}
				value={displayedDashboardName}
				fullWidth
				InputProps={{
					readOnly: true,
					endAdornment: isSelectOpen ? (
						<>
							{isLoading ? (
								<LoadingSpinner withStyle={false} size={15} />
							) : (
								<ArrowDropUp position="end" />
							)}
						</>
					) : (
						<>
							{isLoading ? (
								<LoadingSpinner withStyle={false} size={15} />
							) : (
								<ArrowDropDown position="end" />
							)}
						</>
					),
				}}
			/>

			<Popover
				anchorEl={anchorEl}
				id={'my-default-form-container'}
				//keepMounted
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				open={isSelectOpen}
				onClose={handleSelectClose}
				slotProps={{ paper: { style: { width: '400px', maxWidth: '45vw', maxHeight: '60vh' } } }}
			>
				<List
					component="nav"
					dense={true}
					aria-labelledby="nested-list-subheader"
					onMouseDown={(event) => event.stopPropagation()}
				>
					{menuHasDashboards?.map((menu, index) => (
						<div key={menu.key}>
							<ListItemButton key={menu.key} onClick={() => handleMenuCollapsed(menu.key)}>
								<ListItemText>
									<Typography variant={'h6'} fontSize={16}>
										{menu.name}
									</Typography>
								</ListItemText>
								{!collapseMenus.includes(menu.key) ? <ArrowDropUp /> : <ArrowDropDown />}
							</ListItemButton>

							<Collapse
								id={index}
								in={!collapseMenus.includes(menu.key)}
								timeout="auto"
								unmountOnExit
								sx={{ ml: '20px' }}
							>
								{menu?.items?.map((dashboard) => {
									if (isEmpty(dashboard?.items)) {
										return (
											<ListItemButton
												key={dashboard.dashboard?.key}
												onClick={() => handleSelectDashboard(dashboard.dashboard?.key)}
											>
												<ListItemText>
													<Typography variant={'h5'} fontSize={16}>
														{dashboard.name}
													</Typography>
												</ListItemText>
											</ListItemButton>
										)
									}

									return (
										<div>
											<ListItemButton
												sx={{ display: 'flex' }}
												key={dashboard.dashboard?.key}
												onClick={() => handleSelectDashboard(dashboard.dashboard?.key)}
											>
												<ListItemText>
													<Typography variant={'h5'} fontSize={16}>
														{dashboard.name}
													</Typography>
												</ListItemText>
											</ListItemButton>

											{dashboard?.items?.map((a) => {
												return (
													<ListItemButton
														key={a.dashboard?.key}
														onClick={() => handleSelectDashboard(a.dashboard?.key)}
													>
														<ListItemText sx={{ ml: '20px' }}>
															<Typography variant={'body1'} fontSize={14}>
																{a.name}
															</Typography>
														</ListItemText>
													</ListItemButton>
												)
											})}
										</div>
									)
								})}
							</Collapse>
						</div>
					))}
				</List>
			</Popover>
		</Box>
	)
}

function TileSelectComponent(props) {
	const { onTileChange, selectedTileKey, loadingDashboard, dashboardTileList } = props

	return (
		<Select
			variant="outlined"
			fullWidth
			size="small"
			value={selectedTileKey || ''}
			onChange={(e) => onTileChange(e.target.value)}
			displayEmpty
			IconComponent={
				loadingDashboard ? () => <LoadingSpinner withStyle={false} size={12} /> : ArrowDropDown
			}
		>
			<MenuItem value="">
				<em>Select tile...</em>
			</MenuItem>
			{dashboardTileList.map((t) => (
				<MenuItem key={t.key} value={t.key}>
					{t.name}
				</MenuItem>
			))}
		</Select>
	)
}

function TileFieldMultiSelectComponent(props) {
	const { selectedTile, selectedFields, onFieldsChange } = props

	const settings = useMemo(() => {
		if (selectedTile?.settings && isJson(selectedTile?.settings)) {
			return JSON.parse(selectedTile.settings ?? '{}')
		}

		return {}
	}, [selectedTile?.settings])

	const [open, setOpen] = useState(false)

	const { tileFields, isLoadingFields } = useTileFieldsQuery({
		tileKey: selectedTile?.key,
		tileType: selectedTile?.type,
		tileSetting: settings,
	})

	const handleCloseMenu = (e) => {
		e.stopPropagation()
		setOpen(false)
	}
	return (
		<Select
			variant="outlined"
			fullWidth
			multiple
			size="small"
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			value={selectedFields || []}
			onChange={(e) => onFieldsChange(e.target.value)}
			IconComponent={isLoadingFields ? () => <LoadingSpinner withStyle={false} size={12} /> : null}
		>
			<ListSubheader
				disableSticky
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-end',
					px: 1,
					py: 0.5,
					bgcolor: 'background.paper',
					borderBottom: '1px solid',
					borderColor: 'divider',
					position: 'sticky',
					top: 0,
					zIndex: 1,
				}}
			>
				<IconButton
					size="small"
					onClick={handleCloseMenu}
					onMouseDown={(e) => e.preventDefault()}
					sx={{
						ml: 1,
						color: 'text.secondary',
						'&:hover': { color: 'error.main' },
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</ListSubheader>
			{tileFields?.map((f) => {
				const isSelected = selectedFields?.includes(f.name)
				return (
					<MenuItem
						key={f.name}
						value={f.name}
						sx={{
							fontWeight: isSelected ? 'bold' : 'normal',
							backgroundColor: isSelected ? 'Black' : 'inherit',
						}}
					>
						{f.title}
					</MenuItem>
				)
			})}
		</Select>
	)
}

export default memo(ModalDashboardSettingComponent)
